// The transcript-upload mutation (transcripts.md module plan): create →
// presigned PUT (with progress) → complete, `Idempotency-Key` on both POSTs
// (docs/06 MODE B) — moved verbatim from AgentTranscripts.createTranscript.
// Success = toast + `transcripts.all` invalidation; the drawer close is the
// caller's concern (the form's onSuccess).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import {
  ApiRequestError,
  isApiRequestError,
  requestErrorDescription,
} from "../../lib/errors";
import { createIdempotencyKey } from "../../lib/idempotency";
import { uploadPresignedFile } from "../../lib/presignedUpload";
import { queryKeys } from "../../lib/queryKeys";
import type {
  TranscriptUploadProgress,
  TranscriptUploadSubmission,
  TranscriptUploadSubmit,
} from "../../lib/utteranceDraft";
import { useToast } from "../../ui";

// Re-exported from their spec-named home (transcripts.md: "existing exports
// that move"); the definitions live with the pure helpers in lib/utteranceDraft
// so `validateSubmission` stays React-free.
export type {
  TranscriptUploadProgress,
  TranscriptUploadStatus,
  TranscriptUploadSubmission,
  TranscriptUploadSubmit,
} from "../../lib/utteranceDraft";

type TranscriptCreate = components["schemas"]["TranscriptCreate"];

interface TranscriptMutationVariables {
  idempotencyKey: string;
  progress: TranscriptUploadProgress;
  submission: TranscriptUploadSubmission;
}

function uploadErrorMessage(error: unknown): string {
  return requestErrorDescription(error, "Transcript upload failed.");
}

function rateLimitRetryDelay(error: unknown): number | null {
  if (
    !isApiRequestError(error) ||
    error.status.code !== "RATE_LIMITED" ||
    !error.status.retryable ||
    error.status.retry_after == null
  ) {
    return null;
  }
  return Math.max(error.status.retry_after, 0) * 1_000;
}

const TRANSCRIPT_RATE_LIMIT_RETRIES = 1;

export interface TranscriptUploadHandle {
  submit: TranscriptUploadSubmit;
}

export function useTranscriptUpload(
  agentId: string | undefined,
): TranscriptUploadHandle {
  const queryClient = useQueryClient();
  const toast = useToast();

  const createTranscript = useMutation({
    mutationFn: async ({
      idempotencyKey,
      progress,
      submission,
    }: TranscriptMutationVariables) => {
      if (agentId == null) {
        throw new Error("Select an agent before uploading a transcript.");
      }
      const createBody: TranscriptCreate = {
        agent_id: agentId,
        audio_content_type: submission.mode === "audio" ? "audio/wav" : null,
        audio_intent: submission.mode === "audio" ? "presigned_put" : null,
        audio_source_url: null,
        occurred_at: submission.occurredAt,
        include_marker_ids: submission.includeMarkerIds,
        suppress_marker_ids: submission.suppressMarkerIds,
        utterances: submission.utterances,
        version_tag: submission.versionTag,
      };
      const { data, error } = await apiClient.POST("/v1/transcripts", {
        body: createBody,
        params: {
          header: { "Idempotency-Key": idempotencyKey },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }

      if (submission.mode === "audio") {
        const audioFile = submission.audioFile;
        if (audioFile == null) {
          throw new Error("Select a WAV file.");
        }

        progress({ stage: "requesting_upload_url" });
        const uploadUrl = await apiClient.POST(
          "/v1/transcripts/{id}:create-audio-upload-url",
          {
            params: { path: { id: data.id } },
          },
        );
        if (uploadUrl.error) {
          throw new ApiRequestError(uploadUrl.error);
        }

        progress({ stage: "uploading", uploadPercent: 0 });
        await uploadPresignedFile({
          file: audioFile,
          method: uploadUrl.data.method,
          onProgress: (uploadPercent) => {
            progress({ stage: "uploading", uploadPercent });
          },
          requiredHeaders: uploadUrl.data.required_headers,
          url: uploadUrl.data.url,
        });

        progress({ stage: "completing" });
        const complete = await apiClient.POST(
          "/v1/transcripts/{id}:complete-audio-upload",
          {
            params: {
              header: { "Idempotency-Key": createIdempotencyKey() },
              path: { id: data.id },
            },
          },
        );
        if (complete.error) {
          throw new ApiRequestError(complete.error);
        }
      }

      return data;
    },
    retry: (failureCount, error) =>
      failureCount < TRANSCRIPT_RATE_LIMIT_RETRIES &&
      rateLimitRetryDelay(error) != null,
    retryDelay: (_failureCount, error) => rateLimitRetryDelay(error) ?? 0,
    onError: (error: unknown) => {
      toast.error(uploadErrorMessage(error));
    },
    onSuccess: async () => {
      toast.success("Transcript submitted.");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.transcripts.all,
      });
    },
  });

  return {
    submit: async (submission, progress) => {
      await createTranscript.mutateAsync({
        idempotencyKey: createIdempotencyKey(),
        progress,
        submission,
      });
    },
  };
}
